import { apiService } from './api';

export interface Notification {
  id: number;
  user_id: number;
  type: 'PRICE_ALERT' | 'PORTFOLIO_UPDATE' | 'SYSTEM';
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

export interface CreateNotificationData {
  type: 'PRICE_ALERT' | 'PORTFOLIO_UPDATE' | 'SYSTEM';
  title: string;
  message: string;
}

export interface TelegramConfig {
  chat_id?: string;
  telegram_username?: string;
  bot_token?: string;
  enabled: boolean;
}

export interface TelegramUser {
  id: string;
  chat_id: string;
  username: string;
  telegram_username?: string;
  first_name?: string;
  last_name?: string;
  type: 'app_user' | 'telegram_user';
  is_linked: boolean;
  telegram_enabled: boolean;
}

export interface TelegramUsersResponse {
  users: TelegramUser[];
  total: number;
}

export interface SendMessageData {
  message: string;
  recipients?: string[];  // chat_ids
  type?: 'targeted' | 'broadcast' | 'group';
}

export interface MessageResult {
  sent: number;
  failed: number;
  total: number;
}

class NotificationService {
  async getNotifications(): Promise<Notification[]> {
    return apiService.get<Notification[]>('/notifications');
  }

  async createNotification(data: CreateNotificationData): Promise<Notification> {
    return apiService.post<Notification>('/notifications', data);
  }

  async markAsRead(notificationId: number): Promise<{ message: string }> {
    return apiService.put<{ message: string }>(`/notifications/${notificationId}/read`);
  }

  async markAllAsRead(): Promise<{ message: string }> {
    return apiService.put<{ message: string }>('/notifications/read-all');
  }

  async deleteNotification(notificationId: number): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/notifications/${notificationId}`);
  }

  async getTelegramConfig(): Promise<TelegramConfig> {
    return apiService.get<TelegramConfig>('/notifications/telegram/config');
  }

  async updateTelegramConfig(config: TelegramConfig): Promise<{ message: string }> {
    return apiService.put<{ message: string }>('/notifications/telegram/config', config);
  }

  async testTelegramNotification(): Promise<{ message: string }> {
    return apiService.post<{ message: string }>('/notifications/telegram/test');
  }

  async sendTelegramMessage(message: string): Promise<{ message: string }> {
    return apiService.post<{ message: string }>('/notifications/telegram/send', { message });
  }

  // New enhanced methods
  async getTelegramUsers(): Promise<TelegramUsersResponse> {
    return apiService.get<TelegramUsersResponse>('/notifications/telegram/users');
  }

  async sendTargetedMessage(data: SendMessageData): Promise<{ message: string; result?: MessageResult; sent_count?: number }> {
    return apiService.post<{ message: string; result?: MessageResult; sent_count?: number }>('/notifications/telegram/send-targeted', data);
  }

  async broadcastMessage(message: string): Promise<{ message: string; sent_count: number }> {
    return apiService.post<{ message: string; sent_count: number }>('/notifications/telegram/broadcast', { message });
  }

  async linkTelegramUser(userId: string): Promise<{ message: string; telegram_user: TelegramUser; app_user: any }> {
    return apiService.put<{ message: string; telegram_user: TelegramUser; app_user: any }>(`/notifications/telegram/users/${userId}/link`);
  }

  // Admin methods for managing Telegram users
  async adminGetAllTelegramUsers(): Promise<{
    telegram_users: TelegramUser[];
    app_users_with_telegram: any[];
    total_telegram_users: number;
    total_app_users_with_telegram: number;
  }> {
    return apiService.get<{
      telegram_users: TelegramUser[];
      app_users_with_telegram: any[];
      total_telegram_users: number;
      total_app_users_with_telegram: number;
    }>('/notifications/telegram/admin/users');
  }

  async adminDeleteTelegramUser(userId: string): Promise<{ message: string; deleted_user: TelegramUser }> {
    return apiService.delete<{ message: string; deleted_user: TelegramUser }>(`/notifications/telegram/admin/users/${userId}`);
  }

  async adminRemoveAppTelegramLink(chatId: string): Promise<{ message: string; updated_user: any }> {
    return apiService.put<{ message: string; updated_user: any }>(`/notifications/telegram/admin/users/${chatId}/remove-app-link`);
  }

  async adminReloadChatIds(): Promise<{ message: string; old_count: number; new_count: number; chat_ids: string[] }> {
    return apiService.post<{ message: string; old_count: number; new_count: number; chat_ids: string[] }>('/notifications/telegram/admin/reload-chat-ids');
  }

  // Temporary reload method that works without restart
  async reloadChatIds(): Promise<{ message: string; old_count: number; new_count: number; chat_ids: string[] }> {
    return apiService.post<{ message: string; old_count: number; new_count: number; chat_ids: string[] }>('/notifications/telegram/reload-chat-ids');
  }
}

export const notificationService = new NotificationService(); 